import React, { useState } from 'react';
import axios from 'axios';
import { useLockBodyScroll } from '../utils/useLockBodyScroll';

const CustomTourModal = ({ isOpen, onClose }) => {
  useLockBodyScroll(isOpen);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    tourType: '',
    travelers: '',
    startDate: '',
    duration: '',
    destination: '',
    budget: '',
    interests: [],
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        interests: checked
          ? [...formData.interests, value]
          : formData.interests.filter(i => i !== value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send custom tour request
      await axios.post('/api/contacts', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: 'Custom Tour Request',
        message: `Custom Tour Request Details:
Tour Type: ${formData.tourType}
Travelers: ${formData.travelers}
Start Date: ${formData.startDate}
Duration: ${formData.duration} days
Destination: ${formData.destination}
Budget: ${formData.budget}
Interests: ${formData.interests.join(', ')}
Special Requests: ${formData.specialRequests}`
      });

      // Also send via email service for better formatting
      try {
        await axios.post('/api/communication/custom-tour', {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          country: formData.country,
          tourType: formData.tourType,
          travelers: formData.travelers,
          startDate: formData.startDate,
          duration: formData.duration,
          destination: formData.destination,
          budget: formData.budget,
          interests: formData.interests,
          specialRequests: formData.specialRequests
        });
      } catch (emailError) {
        console.error('Error sending custom tour email:', emailError);
        // Continue even if email fails
      }

      alert('Custom tour request submitted successfully! We will contact you soon with a personalized itinerary.');
      onClose();
      setFormData({
        name: '',
        email: '',
        phone: '',
        country: '',
        tourType: '',
        travelers: '',
        startDate: '',
        duration: '',
        destination: '',
        budget: '',
        interests: [],
        specialRequests: ''
      });
    } catch (error) {
      console.error('Error submitting custom tour request:', error);
      alert('Error submitting request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal active" onClick={onClose}>
      <div className="modal-content bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-gradient-to-r from-brand-green to-brand-teal text-white p-6 rounded-t-2xl z-10">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-3xl font-bold mb-2">Create Your Custom Tour</h3>
              <p className="text-green-100">Tell us your dream vacation and we'll make it happen</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200 text-3xl transform hover:rotate-90 transition">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          <div className="mb-8">
            <div className="flex items-center mb-6">
              <div className="bg-brand-teal text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mr-4">1</div>
              <h4 className="text-2xl font-bold text-gray-800">Basic Information</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Country</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                />
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center mb-6">
              <div className="bg-brand-teal text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mr-4">2</div>
              <h4 className="text-2xl font-bold text-gray-800">Tour Preferences</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Tour Type *</label>
                <select
                  name="tourType"
                  value={formData.tourType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                >
                  <option value="">Select Tour Type</option>
                  <option value="group">Group Tour</option>
                  <option value="fit">FIT (Free Independent Traveler)</option>
                  <option value="git">GIT (Group Inclusive Tour)</option>
                  <option value="individual">Individual Tour</option>
                  <option value="couple">Couple Tour</option>
                  <option value="corporate">Corporate Tour</option>
                  <option value="incentive">Incentive Tour</option>
                  <option value="school">School Trip</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Number of Travelers *</label>
                <input
                  type="number"
                  name="travelers"
                  value={formData.travelers}
                  onChange={handleChange}
                  min="1"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Preferred Start Date *</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Duration (Days) *</label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  min="1"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                />
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center mb-6">
              <div className="bg-brand-teal text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mr-4">3</div>
              <h4 className="text-2xl font-bold text-gray-800">Destination & Budget</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Preferred Destination(s) *</label>
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Paris, Bali, Tokyo"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                />
              </div>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Budget Range (INR) *</label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                >
                  <option value="">Select Budget Range</option>
                  <option value="under-1000">Under ₹10,000</option>
                  <option value="1000-2500">₹10,000 - ₹25,000</option>
                  <option value="2500-5000">₹25,000 - ₹50,000</option>
                  <option value="5000-10000">₹50,000 - ₹1,00,000</option>
                  <option value="over-10000">Over ₹1,00,000</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <div className="flex items-center mb-6">
              <div className="bg-brand-teal text-white w-12 h-12 rounded-full flex items-center justify-center font-bold text-xl mr-4">4</div>
              <h4 className="text-2xl font-bold text-gray-800">Interests & Special Requests</h4>
            </div>
            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-3">Select Your Interests (Multiple)</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {['adventure', 'culture', 'beach', 'wildlife', 'food', 'shopping', 'history', 'photography'].map(interest => (
                  <label key={interest} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="interests"
                      value={interest}
                      checked={formData.interests.includes(interest)}
                      onChange={handleChange}
                      className="w-5 h-5 text-brand-teal rounded focus:ring-brand-teal"
                    />
                    <span className="text-gray-700 capitalize">{interest === 'food' ? 'Food & Wine' : interest}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Special Requests or Additional Details</label>
              <textarea
                name="specialRequests"
                value={formData.specialRequests}
                onChange={handleChange}
                rows="5"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal"
                placeholder="Tell us about dietary restrictions, accessibility needs, specific activities you'd like to include, or any other special requirements..."
              ></textarea>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-brand-green to-brand-teal text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50"
            >
              <i className="fas fa-paper-plane mr-2"></i>
              {isSubmitting ? 'Submitting...' : 'Submit Custom Tour Request'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomTourModal;

